/**
 * Contacts-program `ide.*` navigation handlers (handoff
 * `contacts-identity-ui-lexide-handoff-2026-07-15.md` §2.5).
 *
 * These ops NAVIGATE only — they never mutate. All writes stay in the
 * gateway methods with their confirms. Registered web-only: the
 * platform-ops IM gate keeps them off IM turns (runtime side, C2).
 * Concierge-initiated dispatch rides the captured broadcast context
 * (RT-1) exactly like `ide.toast.show`.
 */
import type { Handler } from "@/lib/platform-ops-registry";
import { navigateTo } from "@/lib/navigation-bridge";

function nav(path: string): ReturnType<Handler> {
  const ok = navigateTo(path);
  return ok
    ? { ok: true, data: { path } }
    : {
        ok: false,
        error: { code: "IDE_NAV_UNAVAILABLE", message: "Router not mounted yet." },
      };
}

/** `ide.contacts.open` — the Contacts list, optional query prefilter. */
export const ideContactsOpenHandler: Handler = (raw) => {
  const query = (raw as { query?: unknown }).query;
  const suffix =
    typeof query === "string" && query.trim().length > 0
      ? `?q=${encodeURIComponent(query.trim())}`
      : "";
  return nav(`/contacts${suffix}`);
};

/** `ide.contacts.detail` — one contact's detail/edit. */
export const ideContactsDetailHandler: Handler = (raw) => {
  const contactId = (raw as { contactId?: unknown }).contactId;
  if (typeof contactId !== "string" || contactId.length === 0) {
    return {
      ok: false,
      error: {
        code: "IDE_INVALID_PARAMS",
        message: "ide.contacts.detail requires a non-empty `contactId`.",
      },
    };
  }
  return nav(`/contacts/${encodeURIComponent(contactId)}`);
};

/** `ide.identity.open` — the Identity pane, card = you | assistant. */
export const ideIdentityOpenHandler: Handler = (raw) => {
  const card = (raw as { card?: unknown }).card;
  const anchor = card === "assistant" ? "assistant" : "you";
  return nav(`/identity?card=${anchor}`);
};

/** `ide.channels.connect` — a channel wizard, optionally at a step. */
export const ideChannelsConnectHandler: Handler = (raw) => {
  const params = raw as { channel?: unknown; step?: unknown };
  const channel =
    params.channel === "telegram" || params.channel === "whatsapp" || params.channel === "email"
      ? params.channel
      : null;
  if (!channel) {
    return {
      ok: false,
      error: {
        code: "IDE_INVALID_PARAMS",
        message: "ide.channels.connect requires channel: telegram | whatsapp | email.",
      },
    };
  }
  const step = typeof params.step === "string" ? `&step=${encodeURIComponent(params.step)}` : "";
  return nav(`/identity?connect=${channel}${step}`);
};

/** `ide.onboarding.open` — the D-13 checklist. */
export const ideOnboardingOpenHandler: Handler = () => nav("/identity?onboarding=1");
